import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatDateWithContext, formatDateTimeWithContext } from '../utils/formatters';

/**
 * Custom hook that combines language and timezone contexts for date formatting
 * Provides timezone-aware date formatting functions that respect user preferences
 */
export const useDateTime = () => {
  const { i18n } = useTranslation();
  const { currentTimezone } = useTimezone();

  // Memoized date formatter that includes user's language and timezone
  const formatDate = useCallback((dateString, customOptions = {}) => {
    if (!dateString) return '-';
    
    const language = i18n.language || 'en';
    const timeZone = customOptions.timeZone || currentTimezone || 'UTC';
    
    return formatDateWithContext(dateString, language, timeZone);
  }, [i18n.language, currentTimezone]);

  // Memoized date-time formatter that includes user's language and timezone
  const formatDateTime = useCallback((dateString, customOptions = {}) => {
    if (!dateString) return '-';
    
    const language = i18n.language || 'en';
    const timeZone = customOptions.timeZone || currentTimezone || 'UTC';
    
    return formatDateTimeWithContext(dateString, language, timeZone);
  }, [i18n.language, currentTimezone]);

  // Format date with custom Intl options while preserving timezone and language
  const formatDateCustom = useCallback((dateString, intlOptions = {}) => {
    if (!dateString) return '-';
    
    const language = i18n.language || 'en';
    const locale = language === 'es' ? 'es-ES' : 'en-US';
    
    const options = {
      timeZone: currentTimezone || 'UTC',
      ...intlOptions
    };
    
    return new Date(dateString).toLocaleDateString(locale, options);
  }, [i18n.language, currentTimezone]);

  // Format date-time with custom Intl options while preserving timezone and language
  const formatDateTimeCustom = useCallback((dateString, intlOptions = {}) => {
    if (!dateString) return '-';
    
    const language = i18n.language || 'en';
    const locale = language === 'es' ? 'es-ES' : 'en-US';
    
    const options = {
      timeZone: currentTimezone || 'UTC',
      ...intlOptions
    };
    
    return new Date(dateString).toLocaleString(locale, options);
  }, [i18n.language, currentTimezone]);

  // Get current timezone info
  const getTimezoneInfo = useCallback(() => {
    return {
      timeZone: currentTimezone || 'UTC',
      language: i18n.language || 'en',
      locale: (i18n.language === 'es') ? 'es-ES' : 'en-US'
    };
  }, [i18n.language, currentTimezone]);

  return {
    formatDate,
    formatDateTime,
    formatDateCustom,
    formatDateTimeCustom,
    getTimezoneInfo,
    currentTimezone,
    currentLanguage: i18n.language
  };
};

export default useDateTime;